import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { NotificationsNone } from '@mui/icons-material';
import requestStatus from 'common/constants/status';
import StyledNotificationList from 'common/theme/notification/StyledNotificationList';

// Skeleton con la FORMA de la fila real (chip + dos líneas + fecha). `LoadingBlock` no
// encaja aquí: arrastra su bloque vacío con ilustración, que no cabe en un panel de 400px.
function ListSkeleton() {
  return (
    <Stack sx={{ p: 2 }} spacing={2.5}>
      {[0, 1, 2, 3].map((row) => (
        <Stack key={row} direction="row" spacing={1.5} alignItems="flex-start">
          <Skeleton variant="circular" width={36} height={36} sx={{ flex: 'none' }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="55%" height={16} />
            <Skeleton variant="text" width="90%" height={14} />
            <Skeleton variant="text" width="30%" height={12} />
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

function EmptyState({ title, body }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={0.75} sx={{ px: 3, py: 6 }}>
      <NotificationsNone sx={{ color: 'text.disabled', fontSize: 40 }} />
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
        {body}
      </Typography>
    </Stack>
  );
}

EmptyState.propTypes = {
  body: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};

function NotificationMenuList({ isValid, renderItems, status, onRetry }) {
  const { t } = useTranslation(['notifications', 'buttons']);

  // El skeleton solo en la PRIMERA carga: al refrescar tras borrar seguimos mostrando la
  // lista para que la bandeja no parpadee.
  const isFirstLoad =
    (status === requestStatus.idle || status === requestStatus.loading) && !isValid;

  if (isFirstLoad) {
    return (
      <StyledNotificationList component="div" aria-busy="true">
        <ListSkeleton />
      </StyledNotificationList>
    );
  }

  if (status === requestStatus.error && !isValid) {
    return (
      <StyledNotificationList component="div">
        <Stack alignItems="center" spacing={1} sx={{ px: 3, py: 6 }}>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
            {t('menu.errorTitle', {
              ns: 'notifications',
              defaultValue: 'No pudimos cargar tus notificaciones'
            })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            {t('menu.errorBody', {
              ns: 'notifications',
              defaultValue: 'Vuelve a intentarlo en un momento.'
            })}
          </Typography>
          <Button
            size="small"
            onClick={onRetry}
            sx={{ color: 'gold.main', fontWeight: 700, textTransform: 'none' }}>
            {t('menu.retry', { ns: 'notifications', defaultValue: 'Reintentar' })}
          </Button>
        </Stack>
      </StyledNotificationList>
    );
  }

  if (!isValid) {
    return (
      <StyledNotificationList component="div">
        <EmptyState
          title={t('emptyTitle', { ns: 'notifications', defaultValue: 'Nada por aquí' })}
          body={t('emptyBody', {
            ns: 'notifications',
            defaultValue: 'Cuando pase algo importante lo verás en esta bandeja.'
          })}
        />
      </StyledNotificationList>
    );
  }

  return <StyledNotificationList component="ul">{renderItems()}</StyledNotificationList>;
}

NotificationMenuList.propTypes = {
  isValid: PropTypes.bool.isRequired,
  onRetry: PropTypes.func.isRequired,
  renderItems: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired
};

export default NotificationMenuList;
